<template>
  <FilterContainer>
    <template #filter>
      <div
        class="flex flex-col gap-2"
        @mousedown.stop
        @click.stop
      >
        <!-- FROM -->
        <label class="flex flex-col gap-2">
          <span class="uppercase text-xs font-bold tracking-wide">
            {{ `${filter.name} - ${__('From')}` }}
          </span>

          <div class="flex">
            <date-picker
              v-model="startValue"
              :clearable="true"
              :color="color"
              compact-time
              :format="format"
              :id="`${field.uniqueKey}-range-start`"
              :initial-value="startFieldValue"
              :input-attrs="{ style: 'direction: ltr; text-align: end;' }"
              inputClass="w-full form-control form-input form-input-bordered"
              :locale="locale"
              :max="max"
              :min="min"
              :placeholder="__('Start')"
              type="datetime"
              @change="handleStartDateChange"
            >
              <template #clear-btn>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 352 512"
                  class="ml-2"
                  style="height: 16px; margin-top: 11px;"
                >
                  <path
                    fill="currentColor"
                    d="M242.72 256l100.07-100.07c12.28-12.28
                    12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28
                    -32.19-12.28-44.48 0L176 189.28 75.93 89.21
                    c-12.28-12.28-32.19-12.28-44.48 0L9.21
                    111.45c-12.28 12.28-12.28 32.19 0
                    44.48L109.28 256 9.21 356.07c-12.28
                    12.28-12.28 32.19 0 44.48l22.24
                    22.24c12.28 12.28 32.2 12.28 44.48
                    0L176 322.72l100.07 100.07c12.28
                    12.28 32.2 12.28 44.48 0l22.24
                    -22.24c12.28-12.28 12.28-32.19
                    0-44.48L242.72 256z"
                  />
                </svg>
              </template>
            </date-picker>
          </div>
        </label>

        <!-- TO -->
        <label class="flex flex-col gap-2">
          <span class="uppercase text-xs font-bold tracking-wide">
            {{ `${filter.name} - ${__('To')}` }}
          </span>

          <div class="flex">
            <date-picker
              v-model="endValue"
              :clearable="true"
              :color="color"
              compact-time
              :format="format"
              :id="`${field.uniqueKey}-range-end`"
              :initial-value="endFieldValue"
              :input-attrs="{ style: 'direction: ltr; text-align: end;' }"
              inputClass="w-full form-control form-input form-input-bordered"
              :locale="locale"
              :max="max"
              :min="min"
              :placeholder="__('End')"
              type="datetime"

              @change="handleEndDateChange"
            >
              <template #clear-btn>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 352 512"
                  class="ml-2"
                  style="height: 16px; margin-top: 11px;"
                >
                  <path
                    fill="currentColor"
                    d="M242.72 256l100.07-100.07
                    c12.28-12.28 12.28-32.19 0
                    -44.48l-22.24-22.24c-12.28
                    -12.28-32.19-12.28-44.48
                    0L176 189.28 75.93 89.21
                    c-12.28-12.28-32.19-12.28
                    -44.48 0L9.21 111.45c-12.28
                    12.28-12.28 32.19 0
                    44.48L109.28 256 9.21
                    356.07c-12.28 12.28-12.28
                    32.19 0 44.48l22.24
                    22.24c12.28 12.28 32.2
                    12.28 44.48 0L176
                    322.72l100.07 100.07
                    c12.28 12.28 32.2
                    12.28 44.48 0l22.24
                    -22.24c12.28-12.28
                    12.28-32.19 0
                    -44.48L242.72 256z"
                  />
                </svg>
              </template>
            </date-picker>
          </div>
        </label>
      </div>
    </template>
  </FilterContainer>
</template>


<script>
import Vue3PersianDatetimePicker from 'vue3-persian-datetime-picker'
import debounce from 'lodash/debounce'
import jMoment from 'moment-jalaali'
import filled from '@/util/filled'

export default {
  emits: ['change'],

  components: {
    DatePicker: Vue3PersianDatetimePicker,
  },

  props: {
    resourceName: {
      type: String,
      required: true,
    },
    filterKey: {
      type: String,
      required: true,
    },
    lens: String,
  },

  data: () => ({
    startValue: null,
    endValue: null,
    debouncedHandleChange: null,
  }),

  created() {
    this.debouncedHandleChange = debounce(() => this.handleChange(), 500)
    this.setCurrentFilterValue()
  },

  mounted() {
    Nova.$on('filter-reset', this.handleFilterReset)
  },

  beforeUnmount() {
    Nova.$off('filter-reset', this.handleFilterReset)
  },

  methods: {
    setCurrentFilterValue() {
      let [startValue, endValue] = this.filter.currentValue || [null, null]

      // Convert Gregorian datetimes to Persian for display
      this.startValue = filled(startValue)
        ? jMoment(startValue).format(this.format)
        : null

      this.endValue = filled(endValue)
        ? jMoment(endValue).format(this.format)
        : null
    },

    validateFilter(startValue, endValue) {
      // Convert Persian datetimes to Gregorian format (YYYY-MM-DD HH:mm:ss) for backend
      startValue = filled(startValue)
        ? jMoment(startValue, this.format).format('YYYY-MM-DD HH:mm:ss')
        : null

      endValue = filled(endValue)
        ? jMoment(endValue, this.format).format('YYYY-MM-DD HH:mm:ss')
        : null

      return [startValue, endValue]
    },

    handleStartDateChange(value) {
      console.log('handleStartDateChange')

      this.startValue = value
      this.handleChange()
    },

    handleEndDateChange(value) {
      console.log('handleEndDateChange')

      this.endValue = value
      this.handleChange()
    },

    handleChange() {

      console.log('handleChange')

      this.$emit('change', {
        filterClass: this.filterKey,
        value: this.validateFilter(this.startValue, this.endValue),
      })
    },

    handleFilterReset() {
      this.setCurrentFilterValue()
    },
  },

  computed: {
    filter() {
      return this.$store.getters[`${this.resourceName}/getFilter`](
        this.filterKey
      )
    },

    field() {
      return this.filter.field
    },

    color() {
      return this.field.color || 'rgb(30, 136, 229)'
    },

    format() {
      return this.field.format || 'jYYYY/jMM/jDD HH:mm'
    },

    locale() {
      return this.field.locale || 'fa,en'
    },

    min() {
      return this.field.min || '1300/01/01 00:00:00'
    },

    max() {
      return this.field.max || '1450/12/29 23:59:59'
    },

    startFieldValue() {
      return this.startValue
    },

    endFieldValue() {
      return this.endValue
    },
  },
}
</script>

<style>
.vpd-main {
  width: 100%;
}

.vpd-next svg,
.vpd-prev svg {
  display: inline;
}
</style>
